import { slot } from "./dom.js";
import { mapLimited } from "./map-limited.js";
import {
  concurrency,
  counted,
  pageFetch,
  type SheetKind,
  sheetTitle,
  type SheetToolProperties,
  startSheetTool,
} from "./sheet-tool.js";
import { GlyphLibrary } from "./worksheet-drawing.js";
import { HanziFacts } from "./worksheet-facts.js";
import { sheetOrder } from "./worksheet-seed.js";
import { type WritingItem, WritingSheet } from "./worksheet-sheet.js";
import {
  changesMoreSettings,
  readWorksheetState,
  worksheetFragment,
  type WorksheetState,
} from "./worksheet-state.js";
import { type SheetCharacter, sheetCharacters } from "./worksheet-words.js";
import type { WorksheetEntries } from "./worksheet-entries.js";

/**
 * The character worksheet generator: the character writing sheet as a kind of
 * sheet `sheet-tool.ts` draws.
 */

/**
 * Each character's entry, its word's and its strokes, read for a writing
 * sheet. The strokes go into the library the sheet is drawn with.
 * @param facts - Each character's 结构 and 部首, from the page.
 */
export async function writingItems(
  characters: readonly SheetCharacter[],
  entries: WorksheetEntries,
  library: GlyphLibrary,
  facts: HanziFacts,
): Promise<readonly WritingItem[]> {
  return mapLimited(
    characters,
    concurrency,
    async (character): Promise<WritingItem> => {
      const [entry, strokes, word] = await Promise.all([
        entries.entry(character.character),
        entries.strokes(character.character),
        character.word === undefined
          ? Promise.resolve(undefined)
          : entries.entry(character.word),
      ]);
      library.hold(character.character, strokes);

      return {
        character: character.character,
        entry,
        word:
          word === undefined ? undefined : { entry: word, at: character.at },
        facts: facts.of(character.character),
      };
    },
  );
}

/** The most characters one sheet takes, which is a few dozen pages. */
export const characterLimit = 300;

/**
 * The character writing sheet.
 * @param facts - Each character's 结构 and 部首, from the page.
 */
export const writingSheetKind = (
  facts: HanziFacts,
): SheetKind<WorksheetState> => ({
  read: readWorksheetState,
  fragment: worksheetFragment,

  nothingAsked: (state) =>
    !state.practice && !state.recall && !state.key
      ? "Tick at least one box under Include."
      : undefined,

  title: (state, list) =>
    sheetTitle(state, list, {
      typed: "Chinese character writing practice",

      saved: "Character writing practice from my saved words",
    }),

  changesMore: changesMoreSettings,

  draw: async ({ state, words, entries, labels }) => {
    const characters = sheetOrder(
      sheetCharacters(words, characterLimit),
      state,
    );
    if (characters.length === 0) {
      return state.source === "typed"
        ? "Type or paste the characters or words you want to practise."
        : "There are no characters to practise.";
    }
    const library = new GlyphLibrary({ document });
    const items = await writingItems(characters, entries, library, facts);

    const pages = new WritingSheet({ library }).pages(state, items, labels);
    const capped =
      characters.length >= characterLimit
        ? ` Only the first ${String(characterLimit)} characters are included.`
        : "";

    return {
      nodes: [library.element, ...pages],
      status: `${counted(items.length, "character", "characters")} on ${counted(pages.length, "page", "pages")}.${capped}`,
    };
  },
});

/** Start the character worksheet generator on its container. */
export function startWorksheetTool(
  container: HTMLElement,
  properties: SheetToolProperties = pageFetch,
): void {
  const facts = slot(container, "hanzi-facts");
  startSheetTool(
    container,
    writingSheetKind(
      facts === undefined
        ? HanziFacts.none()
        : HanziFacts.parse(facts.textContent),
    ),
    properties,
  );
}
